import BeamIcon from '../../../assets/icons/Beam.svg?react';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushDashedRect, pushSegment } from '../glyphs/frame';
import { t } from '../../translation/markers';

const PARAMS = {
  // Vertical bounds, mirroring the floorplan BEAM structure: `height` is the
  // beam's vertical thickness (structure_height), `elevation` the underside
  // above the floor (derived from the structure's base Z at conversion).
  // 2D-only — neither affects the glyph.
  height: { kind: 'float', label: t('height'), default: 0.3, min: 0, decimals: 2, distance: true },
  elevation: { kind: 'float', label: t('bottom elevation'), default: 2.2, min: 0, decimals: 2, distance: true },
} as const;

/**
 * Beam (layout equivalent of a BEAM structure): a LINE run whose bounds draw
 * as a dashed rectangle — the plan convention for an overhead element.
 * `drawLikeWall` gives it the wall-editor draw UX: two clicks (or a drag)
 * between ANY two points, with endpoints snapping onto nearby walls. The
 * centerline (the anchor line) shows while the beam is selected. The vertical
 * bounds (`height`, `bottom elevation`) match the source structure and are
 * kept by the floor→layout conversion.
 */
function buildBeamGlyph(frame: SketchObjectFrame): number[] {
  const out: number[] = [];
  pushDashedRect(out, frame, -frame.width / 2, frame.width / 2, -frame.depth / 2, frame.depth / 2);
  return out;
}

/** Selected only: the beam's centerline (the anchor line between its endpoints). */
function buildBeamSelectionGlyph(frame: SketchObjectFrame): number[] {
  const out: number[] = [];
  pushSegment(out, at(frame, -frame.width / 2, 0), at(frame, frame.width / 2, 0), frame.z);
  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'beam',
    label: t('Beam'),
    category: SketchObjectCategory.STRUCTURE,
    anchor: SketchObjectAnchorKind.LINE,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 2.0, depth: 0.2 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildBeamGlyph },
    // Same icon the STRUCTURE (floorplan) beams use in the panel.
    icon: BeamIcon,
    selectionGlyph: buildBeamSelectionGlyph,
    drawLikeWall: true,
  }),
);
